import { Button, message, Modal, Steps } from 'antd';
import { FC, useEffect, useState } from 'react';
import {
	bulkImport,
	validateImport,
	ValidationResult,
} from '../../hooks/SearchTagsApi';
import { normalizeNationality } from './normalizeNationality';
import { ParsedRow } from './parseSheet';
import { ConfirmStep } from './steps/ConfirmStep';
import { EditStep } from './steps/EditStep';
import { UploadStep } from './steps/UploadStep';
import { EditableRow } from './types';

interface IProps {
	visible: boolean;
	close: () => void;
	import_success: () => void;
}

type StepKey = 'upload' | 'edit' | 'confirm';

const STEPS: Array<{ key: StepKey; title: string }> = [
	{ key: 'upload', title: 'Upload' },
	{ key: 'edit', title: 'Revisão' },
	{ key: 'confirm', title: 'Confirmação' },
];

function toEditableRows(parsed: ParsedRow[]): EditableRow[] {
	return parsed.map((row) => ({
		nationality: normalizeNationality(row.nationality) || '',
		document: row.document,
		title: row.title,
		email: row.email,
	}));
}

export const AuthorImportModal: FC<IProps> = ({
	visible,
	close,
	import_success,
}) => {
	const [stepKey, setStepKey] = useState<StepKey>('upload');
	const [rows, setRows] = useState<EditableRow[]>([]);
	const [validations, setValidations] = useState<ValidationResult[]>([]);
	const [confirmedOverwrites, setConfirmedOverwrites] = useState<Set<number>>(
		new Set()
	);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		if (!visible) {
			setStepKey('upload');
			setRows([]);
			setValidations([]);
			setConfirmedOverwrites(new Set());
			setLoading(false);
		}
	}, [visible]);

	const goToEdit = (parsed: ParsedRow[]) => {
		setRows(toEditableRows(parsed));
		setStepKey('edit');
	};

	const runValidation = async () => {
		const cleaned = rows.filter(
			(r) => r.nationality || r.document || r.title || r.email
		);
		if (cleaned.length === 0) {
			message.warning('Adicione pelo menos uma linha para validar.');
			return;
		}
		setLoading(true);
		try {
			const result = await validateImport(
				'author',
				cleaned.map((r) => ({
					nationality: r.nationality,
					document: r.document,
					title: r.title,
					email: r.email || undefined,
				}))
			);
			setRows(cleaned);
			setValidations(result);
			setConfirmedOverwrites(new Set());
			setStepKey('confirm');
		} catch (err) {
			console.error('Erro ao validar:', err);
			message.error('Erro ao validar importação.');
		} finally {
			setLoading(false);
		}
	};

	const toggleOverwrite = (index: number, checked: boolean) => {
		const next = new Set(confirmedOverwrites);
		if (checked) next.add(index);
		else next.delete(index);
		setConfirmedOverwrites(next);
	};

	const confirmImport = async () => {
		const willImport = validations.filter(
			(v) =>
				v.status === 'ok' ||
				v.status === 'warning' ||
				(v.status === 'overwrite' && confirmedOverwrites.has(v.index))
		);
		if (willImport.length === 0) {
			message.warning('Nenhuma linha pronta para importar.');
			return;
		}
		setLoading(true);
		try {
			const response = await bulkImport(
				'author',
				rows.map((r) => ({
					nationality: r.nationality,
					document: r.document,
					title: r.title,
					email: r.email || undefined,
				})),
				Array.from(confirmedOverwrites)
			);
			message.success(
				`Importação concluída: ${response.imported} novos, ${response.overwritten} sobrescritos, ${response.skipped.length} ignorados.`
			);
			import_success();
			close();
		} catch (err) {
			console.error('Erro ao importar:', err);
			message.error('Erro ao importar autores.');
		} finally {
			setLoading(false);
		}
	};

	const currentStepIndex = STEPS.findIndex((s) => s.key === stepKey);

	const footer: JSX.Element[] = [];
	if (stepKey === 'edit') {
		footer.push(
			<Button
				key='back-to-upload'
				onClick={() => setStepKey('upload')}
				disabled={loading}
			>
				Voltar
			</Button>
		);
		footer.push(
			<Button
				key='validate'
				type='primary'
				onClick={runValidation}
				loading={loading}
			>
				Validar
			</Button>
		);
	}
	if (stepKey === 'confirm') {
		footer.push(
			<Button
				key='back-to-edit'
				onClick={() => setStepKey('edit')}
				disabled={loading}
			>
				Voltar
			</Button>
		);
		footer.push(
			<Button
				key='confirm-import'
				type='primary'
				onClick={confirmImport}
				loading={loading}
			>
				Confirmar Importação
			</Button>
		);
	}

	return (
		<Modal
			visible={visible}
			centered
			closable
			destroyOnClose
			maskClosable={false}
			width={1000}
			onCancel={() => close()}
			title='Importar Tags de Autor'
			footer={footer.length > 0 ? footer : null}
		>
			<Steps current={currentStepIndex} size='small' className='mb-6'>
				{STEPS.map((s) => (
					<Steps.Step key={s.key} title={s.title} />
				))}
			</Steps>

			{stepKey === 'upload' && <UploadStep onParsed={goToEdit} />}
			{stepKey === 'edit' && (
				<EditStep rows={rows} onChange={setRows} loading={loading} />
			)}
			{stepKey === 'confirm' && (
				<ConfirmStep
					rows={rows}
					validations={validations}
					confirmedOverwrites={confirmedOverwrites}
					onToggleOverwrite={toggleOverwrite}
				/>
			)}
		</Modal>
	);
};
